import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Send, Paperclip, Eye, X, RefreshCw, ChevronDown, Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import RichTextEditor from '../ui/RichTextEditor';
import PoweredByFooter from '../shared/PoweredByFooter';

import BundleAttachmentPicker from './BundleAttachmentPicker';

const SaleAuthorizationEmailDialog = ({ open, onClose, deliverableId, onSent, saleId, ownerId }) => {
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [form, setForm] = useState({ to: '', cc: '', bcc: '', display_name: '', reply_to: '', subject: '', body: '', from_email: 'mailbox@mygreendoorapp.com' });
  const [attachment, setAttachment] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showVersions, setShowVersions] = useState(false);
  const [extraFileIds, setExtraFileIds] = useState([]);

  useEffect(() => {
    if (open && deliverableId) { setExtraFileIds([]); loadDraft(); }
  }, [open, deliverableId]); // eslint-disable-line

  const loadDraft = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/sale-authorizations/email-draft/${deliverableId}`);
      const d = res.data;
      setForm({ to: d.to || '', cc: d.cc || '', bcc: d.bcc || '', display_name: d.display_name || '', reply_to: d.reply_to || '', subject: d.subject || '', body: d.body || '', from_email: d.from_email || 'mailbox@mygreendoorapp.com' });
      const vs = d.authorization_versions || [];
      setVersions(vs);
      if (d.authorization_file_id) {
        const latest = vs.find(v => v.file_id === d.authorization_file_id) || vs[vs.length - 1];
        setAttachment({ file_id: d.authorization_file_id, filename: d.authorization_filename || 'authorization.pdf', version_label: latest?.label || 'Draft' });
      }
    } catch { toast.error('Failed to load email draft'); }
    finally { setLoading(false); }
  };

  const handleRegenerateNew = async () => {
    setRegenerating(true);
    try {
      const res = await api.post(`/sale-authorizations/generate/${deliverableId}`);
      toast.success(`New version generated (${res.data.version_label})`);
      await loadDraft();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate new version'); }
    finally { setRegenerating(false); }
  };

  const selectVersion = (v) => {
    setAttachment({ file_id: v.file_id, filename: v.filename, version_label: v.label });
    setShowVersions(false);
  };

  const openAttachment = () => {
    if (!attachment?.file_id) return;
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/files/download/${attachment.file_id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => { const url = URL.createObjectURL(blob); window.open(url, '_blank'); });
  };

  const handleSend = async () => {
    if (!form.to.trim()) { toast.error('Recipient email is required'); return; }
    setSending(true);
    try {
      await api.post(`/sale-authorizations/send-email/${deliverableId}`, {
        ...form,
        attach_pdf: !!attachment,
        authorization_file_id: attachment?.file_id,
        extra_file_ids: extraFileIds,
      });
      toast.success('Authorization email sent');
      onSent?.(); onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send email'); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Send Authorization Email</DialogTitle></DialogHeader>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="font-semibold text-right">To</Label><Input value={form.to} onChange={e => setForm({ ...form, to: e.target.value })} placeholder="recipient@example.com" /></div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="text-right text-muted-foreground">CC</Label><Input value={form.cc} onChange={e => setForm({ ...form, cc: e.target.value })} /></div>
            <div className="border rounded-md p-3 space-y-2 bg-muted/20">
              <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="font-semibold text-right">From</Label><span className="text-sm text-muted-foreground">{form.from_email}</span></div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4"><Label className="text-right text-sm text-muted-foreground">Display Name</Label><Input value={form.display_name} onChange={e => setForm({ ...form, display_name: e.target.value })} className="h-8" /></div>
              <div className="grid grid-cols-[80px_1fr] items-center gap-2 pl-4"><Label className="text-right text-sm text-muted-foreground">Reply To</Label><Input value={form.reply_to} onChange={e => setForm({ ...form, reply_to: e.target.value })} className="h-8" /></div>
            </div>
            <div className="grid grid-cols-[80px_1fr] items-center gap-2"><Label className="font-semibold text-right">RE:</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} /></div>
            <RichTextEditor value={form.body} onChange={v => setForm({ ...form, body: v })} />
            <PoweredByFooter />

            {/* Attachment with version management */}
            <div className="border rounded-md p-3 bg-muted/20 space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-muted-foreground uppercase">Attachment</p>
                <div className="flex gap-2">
                  {versions.length > 1 && (
                    <Button variant="ghost" size="sm" className="h-7 text-xs gap-1" onClick={() => setShowVersions(!showVersions)}>
                      <ChevronDown className={`w-3 h-3 transition-transform ${showVersions ? 'rotate-180' : ''}`} />
                      {versions.length} versions
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={handleRegenerateNew} disabled={regenerating}>
                    {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                    Generate New
                  </Button>
                </div>
              </div>

              {showVersions && versions.length > 0 && (
                <div className="space-y-1 border rounded p-2 bg-background">
                  {[...versions].reverse().map(v => (
                    <button key={v.file_id} onClick={() => selectVersion(v)}
                      className={`w-full text-left text-xs px-2 py-1.5 rounded hover:bg-muted flex items-center justify-between ${attachment?.file_id === v.file_id ? 'bg-muted font-semibold' : ''}`}>
                      <span>{v.label}</span>
                      <span className="text-muted-foreground">{v.generated_at ? new Date(v.generated_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}</span>
                    </button>
                  ))}
                </div>
              )}

              {attachment ? (
                <div className="flex items-center gap-3 p-2 border rounded bg-white">
                  <Paperclip className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm truncate block">{attachment.filename}</span>
                    {attachment.version_label && <span className="text-xs text-muted-foreground">{attachment.version_label}</span>}
                  </div>
                  <Button variant="ghost" size="sm" className="h-7 px-2" onClick={openAttachment} title="Preview"><Eye className="w-3.5 h-3.5" /></Button>
                  <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => setAttachment(null)} title="Remove"><X className="w-3.5 h-3.5" /></Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground italic">No attachment — select a version above or generate a new one.</p>
              )}
            </div>

            {/* Bundle attachment picker (Components 2 & 3) */}
            {saleId && ownerId && (
              <BundleAttachmentPicker
                saleId={saleId}
                ownerId={ownerId}
                excludeFileIds={attachment?.file_id ? [attachment.file_id] : []}
                onChange={setExtraFileIds}
              />
            )}

            {/* Auto-publish note */}
            <div className="flex items-start gap-2 text-xs text-muted-foreground bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
              <Globe className="w-3.5 h-3.5 shrink-0 mt-0.5 text-blue-500" />
              <span>Sending this email will automatically publish the authorization to the owner portal.</span>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)} disabled={sending}>Cancel</Button>
          <Button onClick={handleSend} disabled={sending || loading} className="bg-primary hover:bg-primary/90 text-white">
            {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SaleAuthorizationEmailDialog;
